import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deviceAssignmentSchema, deviceUnassignSchema } from '@fleet/shared';
import { telematicsApi, type TelematicsDeviceView } from '../../api/telematics';
import { vehiclesApi } from '../../api/vehicles';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { DateTimeInput, Field, inputClass } from '../../ui/Field';
import { ErrorNotice } from '../../ui/ErrorNotice';
import { combine, dateValue, formatInstant, timeValue } from '../../ui/datetime';

type Mode = 'assign' | 'end';

/**
 * Einbauen, Umstecken und Ausbauen in einem Dialog.
 *
 * Zusammen, weil alle drei dieselbe heikle Angabe brauchen: den Zeitpunkt. Er
 * entscheidet, welchen Fahrten die Zuordnung gilt, und ist deshalb nie
 * vorbelegt mit „jetzt und fertig", sondern sichtbar und änderbar.
 *
 * Datum und Uhrzeit stehen in **lokaler** Zeit; umgerechnet wird in
 * `ui/datetime`, dieselbe Stelle wie beim Verschieben einer Buchung.
 */
export function DeviceAssignmentDialog({
  device,
  mode,
  onClose,
}: {
  device: TelematicsDeviceView;
  mode: Mode;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const now = new Date();

  const [vehicleId, setVehicleId] = useState('');
  const [date, setDate] = useState(dateValue(now));
  const [time, setTime] = useState(timeValue(now));
  const [note, setNote] = useState('');

  // Nur aktive Fahrzeuge: Ein stillgelegtes würde der Server ohnehin abweisen.
  const vehicles = useQuery({
    queryKey: ['vehicles', { includeInactive: false }],
    queryFn: () => vehiclesApi.list(false),
    enabled: mode === 'assign',
  });

  const instant = combine(date, time);
  const current = device.currentAssignment;

  const problems: string[] = [];
  if (!instant) problems.push('Bitte Datum und Uhrzeit vollständig angeben.');
  if (mode === 'assign' && !vehicleId) problems.push('Bitte ein Fahrzeug auswählen.');
  /**
   * Die Zukunft wird schon hier abgefangen, nicht erst am Server. Eine
   * Zuordnung, die morgen beginnt, erklärt die heute eingegangenen Rohdaten
   * nicht — und ein Formular, das den Wert erst annimmt und dann mit einem
   * Fehler zurückkommt, wirkt kaputt.
   */
  if (instant && instant > now) problems.push('Der Zeitpunkt darf nicht in der Zukunft liegen.');
  if (mode === 'end' && instant && current && instant <= new Date(current.validFrom)) {
    problems.push('Der Ausbau muss nach dem Einbau liegen.');
  }

  const submit = useMutation({
    mutationFn: async () => {
      if (!instant) return;

      if (mode === 'assign') {
        // Dasselbe Schema, das am Server prüft — es kann hier nicht abweichen.
        const parsed = deviceAssignmentSchema.parse({
          vehicleId,
          validFrom: instant,
          note: note.trim() || undefined,
        });
        await telematicsApi.assign(device.id, parsed);
      } else {
        const parsed = deviceUnassignSchema.parse({
          validTo: instant,
          note: note.trim() || undefined,
        });
        await telematicsApi.endAssignment(device.id, parsed);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['telematics-devices'] });
      await queryClient.invalidateQueries({ queryKey: ['device-assignments', device.id] });
      // Das Logger-Abzeichen in der Fahrzeugliste hängt an derselben Tabelle.
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      onClose();
    },
  });

  const title = mode === 'end' ? 'Gerät ausbauen' : current ? 'Gerät umstecken' : 'Gerät einbauen';

  const description =
    mode === 'end'
      ? `${device.imei} ist seit ${formatInstant(current!.validFrom)} in ${current!.vehicle.licensePlate} montiert.`
      : current
        ? `${device.imei} steckt derzeit in ${current.vehicle.licensePlate}. Diese Zuordnung wird zum gewählten Zeitpunkt beendet.`
        : `${device.imei} ist derzeit keinem Fahrzeug zugeordnet.`;

  return (
    <Modal title={title} description={description} onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (problems.length === 0) void submit.mutateAsync();
        }}
        className="space-y-4"
      >
        {mode === 'assign' && (
          <Field label="Fahrzeug">
            <select
              value={vehicleId}
              onChange={(event) => setVehicleId(event.target.value)}
              className={inputClass}
              autoFocus
            >
              <option value="">Bitte wählen …</option>
              {vehicles.data
                ?.filter((vehicle) => vehicle.id !== current?.vehicle.id)
                .map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id} disabled={vehicle.hasDevice}>
                    {vehicle.licensePlate} — {vehicle.displayName}
                    {vehicle.hasDevice ? ' (hat bereits ein Gerät)' : ''}
                  </option>
                ))}
            </select>
          </Field>
        )}

        <Field
          label={mode === 'end' ? 'Ausbau am' : 'Einbau am'}
          hint="Darf zurückdatiert werden — Rohdaten liegen meist vor der Eintragung."
        >
          <DateTimeInput
            date={date}
            time={time}
            onDateChange={setDate}
            onTimeChange={setTime}
            maxDate={dateValue(now)}
            stepSeconds={60}
            label={mode === 'end' ? 'Ausbau' : 'Einbau'}
          />
        </Field>

        <Field label="Notiz" optional hint="Etwa der Grund des Umsteckens.">
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className={inputClass}
            maxLength={500}
          />
        </Field>

        {problems.length > 0 && (
          <ul className="list-inside list-disc rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        )}

        {submit.isError && <ErrorNotice error={submit.error} />}

        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            type="submit"
            variant={mode === 'end' ? 'danger' : 'primary'}
            disabled={problems.length > 0 || submit.isPending}
          >
            {mode === 'end' ? 'Ausbauen' : current ? 'Umstecken' : 'Einbauen'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
